import { createFileRoute, redirect } from "@tanstack/react-router";
import {Bell, ChevronRight, Motorbike, Store} from "lucide-react";
import { ConfirmSheet } from "@/components/confirm-send-sheet";
import { RecipientInfoSheet } from "@/components/recipient-info-sheet";
import { OriginAddressSheet } from "@/components/origin-address-sheet";
import { ConfirmAddressSheet } from "@/components/confirm-address-sheet";
import { NotificationsSheet } from "@/components/notifications-sheet";
import { Logo } from "@/components/core/logo";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/base";
import { useDelivery } from "@/contexts/delivery.context";
import { shouldShowTour } from "@/contexts/tour.context";
import { useTour } from "@/hooks/useTour";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/app/")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (!token || !user) {
      throw redirect({
        to: "/auth/identify",
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const delivery = useDelivery();
  const tour = useTour();
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Iniciar tour automaticamente no primeiro acesso
  useEffect(() => {
    // Aguardar um pouco para garantir que a página carregou
    const timer = setTimeout(() => {
      if (shouldShowTour() && !tour.running) {
        tour.start();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <section className="flex justify-between items-center px-6 py-4 max-w-2xl mx-auto">
          <div data-tour="logo">
            <Logo />
          </div>
          <button
            onClick={() => setNotificationsOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Bell className="size-6 text-gray-700" />
          </button>
        </section>
      </header>

      <section className="flex flex-col gap-6 py-6 max-w-2xl mx-auto">

        <div className="relative px-6">
          <div className="absolute inset-0 bg-linear-to-b from-primary/5 via-orange-50/30 to-transparent rounded-3xl -z-10" />

          <div className="flex justify-center mb-4">
            <div className="relative w-48 h-48">
              <div className="absolute inset-0 bg-linear-to-br from-primary/10 to-orange-400/10 rounded-full blur-3xl" />
              <img
                src="/entregador.png"
                alt="Entregador"
                className="relative w-full h-full object-contain drop-shadow-xl"
              />
            </div>
          </div>
          <div className="text-center mb-6">
            <h3 className="text-2xl font-semibold text-gray-900 mb-1">
              Envie ou receba
            </h3>
            <p className="text-sm text-gray-500">
              Rápido e seguro de onde estiver
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
            <button
              data-tour="send-button"
              className="w-full flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100 group"
              onClick={() => {
                delivery.setDeliveryType("send");
                delivery.openOriginSheet();
              }}
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
                <Motorbike className="size-6 text-primary" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-base font-semibold text-gray-900">
                  Enviar
                </p>
                <p className="text-sm text-gray-500">
                  Entregue em minutos
                </p>
              </div>
              <ChevronRight className="size-5 text-gray-400 group-hover:text-primary transition-colors" />
            </button>

            <button
              data-tour="receive-button"
              className="w-full flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors group"
              onClick={() => {
                delivery.setDeliveryType("receive");
                delivery.openOriginSheet();
              }}
            >
              <div className="w-12 h-12 rounded-2xl bg-orange-500/10 flex items-center justify-center shrink-0 group-hover:bg-orange-500/15 transition-colors">
                <Store className="size-6 text-orange-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-base font-semibold text-gray-900">
                  Receber
                </p>
                <p className="text-sm text-gray-500">
                  Busque onde quiser
                </p>
              </div>
              <ChevronRight className="size-5 text-gray-400 group-hover:text-orange-600 transition-colors" />
            </button>
          </div>
        </div>
      </section>

      {delivery.addressSheetMode === "origin" && delivery.deliveryType === "send" ? (
        <OriginAddressSheet
          open={delivery.recipientInfoSheetOpen}
          onOpenChange={delivery.setRecipientInfoSheetOpen}
          autoOpenAddForm={delivery.originSheetForceAddForm}
        />
      ) : (
        <RecipientInfoSheet
          open={delivery.recipientInfoSheetOpen}
          onOpenChange={delivery.setRecipientInfoSheetOpen}
          deliveryType={delivery.deliveryType || "send"}
          sheetTitle={
            delivery.addressSheetMode === "origin"
              ? "Onde será a retirada?"
              : delivery.addressSheetMode === "destination"
                ? "Informações do destino"
                : undefined
          }
          sheetDescription={
            delivery.addressSheetMode === "origin"
              ? "Selecione quem entregará sua encomenda"
              : delivery.addressSheetMode === "destination"
                ? "Selecione o endereço para receber a encomenda"
                : undefined
          }
        />
      )}
      {delivery.confirmAddressType && (
        <ConfirmAddressSheet
          open={delivery.confirmAddressSheetOpen}
          onOpenChange={delivery.setConfirmAddressSheetOpen}
          type={delivery.confirmAddressType}
        />
      )}
      <ConfirmSheet
        open={delivery.confirmSheetOpen}
        onOpenChange={delivery.setConfirmSheetOpen}
      />
      <NotificationsSheet
        open={notificationsOpen}
        onOpenChange={setNotificationsOpen}
      />
    </main>
  );
}
